import type { ApprovalComment, ReportWorkflowItem, ReportWorkflowStatus } from "./types";

const items: ReportWorkflowItem[] = [];

let dummyDataSeeded = false;

/** Call once to seed dummy reports for demo. Pass a blob URL for file preview. */
export function seedDummyDataIfNeeded(fileDataUrl: string): void {
  if (dummyDataSeeded) return;
  dummyDataSeeded = true;
  const base = "2025-03-01T10:00:00.000Z";
  const report1: ReportWorkflowItem = {
    id: "rw-dummy-1",
    reportName: "Q1 2025 Performance Summary",
    description: "Quarterly performance metrics including KPIs, initiatives progress, and department highlights for executive review.",
    fileName: "Q1-2025-Performance.pdf",
    fileDataUrl,
    status: "pending_manager",
    submittedAt: base,
  };
  const report2: ReportWorkflowItem = {
    id: "rw-dummy-2",
    reportName: "Annual Strategy Review 2025",
    description: "Full-year strategy alignment report with objectives, targets, and recommendations for next cycle.",
    fileName: "Strategy-Review-2025.pdf",
    fileDataUrl,
    status: "pending_final",
    submittedAt: "2025-03-05T09:00:00.000Z",
    managerComment: {
      stage: "manager",
      approved: true,
      comment: "Reviewed and approved. Ready for final sign-off.",
      at: "2025-03-06T14:00:00.000Z",
    },
  };
  const report3: ReportWorkflowItem = {
    id: "rw-dummy-3",
    reportName: "Budget Allocation Report",
    description: "Proposed budget allocation across divisions for FY2025 with justification and impact analysis.",
    fileName: "Budget-Allocation-2025.pdf",
    fileDataUrl,
    status: "approved",
    submittedAt: "2025-02-20T11:00:00.000Z",
    managerComment: {
      stage: "manager",
      approved: true,
      comment: "Numbers verified. Approved for final review.",
      at: "2025-02-25T10:00:00.000Z",
    },
    headComment: {
      stage: "head",
      approved: true,
      comment: "Final approval granted. Proceed with implementation.",
      at: "2025-03-01T09:00:00.000Z",
    },
  };
  const report4: ReportWorkflowItem = {
    id: "rw-dummy-4",
    reportName: "Marketing Campaign Proposal",
    description: "Q2 marketing campaign proposal with channels, timelines, and budget.",
    fileName: "Marketing-Proposal-Q2.pdf",
    fileDataUrl,
    status: "rejected_by_manager",
    submittedAt: "2025-02-28T15:00:00.000Z",
    managerComment: {
      stage: "manager",
      approved: false,
      comment: "Please revise the budget section and add competitor analysis before resubmission.",
      at: "2025-03-02T11:00:00.000Z",
    },
  };
  items.push(report1, report2, report3, report4);
}

function nextId(): string {
  return `rw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function addReport(item: Omit<ReportWorkflowItem, "id" | "status" | "submittedAt">): ReportWorkflowItem {
  const newItem: ReportWorkflowItem = {
    ...item,
    id: nextId(),
    status: "pending_manager",
    submittedAt: new Date().toISOString(),
  };
  items.push(newItem);
  return newItem;
}

export function getReport(id: string): ReportWorkflowItem | undefined {
  return items.find((r) => r.id === id);
}

export function getReportsByStatus(status: ReportWorkflowStatus): ReportWorkflowItem[] {
  return items.filter((r) => r.status === status);
}

export function getReportsPendingManager(): ReportWorkflowItem[] {
  return getReportsByStatus("pending_manager");
}

export function getReportsPendingFinal(): ReportWorkflowItem[] {
  return getReportsByStatus("pending_final");
}

export function setManagerDecision(
  id: string,
  approved: boolean,
  comment: string
): ReportWorkflowItem | undefined {
  const report = items.find((r) => r.id === id);
  if (!report || report.status !== "pending_manager") return undefined;
  report.managerComment = {
    stage: "manager",
    approved,
    comment,
    at: new Date().toISOString(),
  };
  report.status = approved ? "pending_final" : "rejected_by_manager";
  return report;
}

export function setHeadDecision(
  id: string,
  approved: boolean,
  comment: string
): ReportWorkflowItem | undefined {
  const report = items.find((r) => r.id === id);
  if (!report || report.status !== "pending_final") return undefined;
  report.headComment = {
    stage: "head",
    approved,
    comment,
    at: new Date().toISOString(),
  };
  report.status = approved ? "approved" : "rejected_final";
  return report;
}

export function getAllReports(): ReportWorkflowItem[] {
  return [...items];
}
